import type { adminConsole as en } from "../en/admin-console";
import type { Messages } from "../types";

/** واجهة لوحة الإدارة + تبويبات الطلبات ومراجعة التقويم والنظام (ALP-85). */
export const adminConsole: Messages<typeof en> = {
  product: "إدارة ملفك",
  staffOnly: "للفريق فقط",
  env: "البيئة {{env}}",
  backToApp: "العودة إلى التطبيق",
  signedInAs: "مسجّل الدخول باسم",
  signOut: "تسجيل الخروج",
  switchLang: "English",
  theme: "المظهر",
  themeLight: "فاتح",
  themeDark: "داكن",
  themeSystem: "حسب النظام",
  menu: "قائمة الحساب",
  tabs: {
    label: "أقسام الإدارة",
    overview: "نظرة عامة",
    requests: "الطلبات",
    users: "المستخدمون",
    orgs: "المؤسسات",
    plans: "الخطط",
    ai: "نماذج الذكاء الاصطناعي",
    aiActivity: "نشاط الذكاء الاصطناعي",
    flows: "التدفقات",
    audit: "التدقيق",
    calendar: "مراجعة التقويم",
    settings: "الإعدادات ومفاتيح الإيقاف",
    system: "النظام",
  },
  badge_zero: "{{count}} جديد",
  badge_one: "{{count}} جديد",
  badge_two: "{{count}} جديد",
  badge_few: "{{count}} جديد",
  badge_many: "{{count}} جديد",
  badge_other: "{{count}} جديد",
  shortcuts: {
    title: "اختصارات لوحة الإدارة",
    open: "اختصارات لوحة المفاتيح",
    hint: "اضغط g ثم الحرف لفتح التبويب.",
    help: "عرض هذه القائمة",
    back: "العودة إلى التطبيق",
  },
  notifications: {
    title: "إشعارات الفريق",
    open: "الإشعارات",
    unread_zero: "{{count}} غير مقروء",
    unread_one: "{{count}} غير مقروء",
    unread_two: "{{count}} غير مقروء",
    unread_few: "{{count}} غير مقروء",
    unread_many: "{{count}} غير مقروء",
    unread_other: "{{count}} غير مقروء",
    empty: "لا جديد.",
    markAll: "تعليم الكل كمقروء",
  },
  requests: {
    title: "طلبات الوصول",
    description: "أشخاص طلبوا استخدام ملفك. تواصل معهم، دوّن الملاحظات، ثم وافق بخطة أو ارفض.",
    search: "ابحث بالبريد أو اسم المستخدم أو الشركة أو الهاتف",
    filter: { all: "الكل", new: "جديد", contacted: "تم التواصل", approved: "مقبول", rejected: "مرفوض" },
    col: { person: "مقدّم الطلب", company: "الشركة", phone: "الهاتف", reason: "السبب", status: "الحالة", received: "وصل" },
    empty: "لا توجد طلبات هنا.",
    emptyHint: "تظهر طلبات التسجيل الجديدة هنا فور وصولها.",
    open: "فتح طلب {{username}}",
    notes_zero: "ملاحظات: {{count}}",
    notes_one: "ملاحظات: {{count}}",
    notes_two: "ملاحظات: {{count}}",
    notes_few: "ملاحظات: {{count}}",
    notes_many: "ملاحظات: {{count}}",
    notes_other: "ملاحظات: {{count}}",
    status: { new: "جديد", contacted: "تم التواصل", approved: "مقبول", rejected: "مرفوض" },
    detail: {
      title: "@{{username}}",
      submitted: "أُرسل {{when}}",
      email: "البريد الإلكتروني",
      phone: "الهاتف",
      company: "الشركة",
      heardFrom: "عرف عنا من",
      language: "اللغة",
      reason: "لماذا ملفك",
      consent: "وافق على الشروط وسياسة الخصوصية {{when}}",
      none: "—",
      notes: "الملاحظات",
      notePlaceholder: "أضف ملاحظة داخلية (يراها الفريق فقط)",
      addNote: "إضافة ملاحظة",
      noteAdded: "أُضيفت الملاحظة",
      log: "سجل التواصل",
      logEmpty: "لا يوجد نشاط بعد.",
      decided: "تقرّر {{when}}",
      account: "الحساب",
      openOrg: "فتح المؤسسة",
      rejectReason: "السبب المذكور",
    },
    event: { submitted: "أُرسل الطلب", contacted: "تم التواصل", approved: "تمت الموافقة", rejected: "رُفض", reopened: "أُعيد فتحه" },
    by: "بواسطة {{who}}",
    channel: { phone: "مكالمة هاتفية", email: "بريد إلكتروني", whatsapp: "واتساب", meeting: "اجتماع", other: "غير ذلك" },
    actions: {
      approve: "موافقة",
      reject: "رفض",
      contacted: "تم التواصل",
      call: "اتصال",
      email: "بريد",
    },
    approve: {
      title: "الموافقة على @{{username}}",
      description: "يُنشئ الحساب (بكلمة المرور التي اختارها) ومؤسسة يملكها. يمكنه تسجيل الدخول فورًا.",
      plan: "الخطة",
      orgName: "اسم المؤسسة",
      note: "ملاحظة (اختياري)",
      confirm: "موافقة وإنشاء الحساب",
      done: "تمت الموافقة: {{org}} جاهزة على خطة {{plan}}",
    },
    reject: {
      title: "رفض @{{username}}",
      description: "سيرى «لم تتم الموافقة» عند محاولة تسجيل الدخول. يبقى السبب داخليًا.",
      confirm: "رفض الطلب",
      done: "رُفض الطلب",
    },
    contact: {
      title: "تسجيل تواصل مع @{{username}}",
      channel: "الوسيلة",
      detail: "ملخص (اختياري)",
      detailPlaceholder: "عمّ تحدثتم؟",
      confirm: "حفظ",
      done: "سُجّل التواصل",
    },
  },
  calendar: {
    title: "مراجعة التقويم",
    description: "أحداث عامة تحتاج إلى قرار. النشر أو الرفض يسري على كل المؤسسات ويبقى بعد التحديثات.",
    filter: { needs_review: "بحاجة لمراجعة", published: "منشور", rejected: "مرفوض", all: "الكل" },
    search: "ابحث في الأحداث أو رموز الدول",
    empty: "لا شيء بانتظار المراجعة.",
    scope: "النطاق",
    global: "عالمي",
    confidence: "الثقة {{value}}",
    source: "المصدر",
    publish: "نشر للجميع",
    reject: "رفض للجميع",
    publishTitle: "نشر «\u2068{{title}}\u2069»؟",
    rejectTitle: "رفض «\u2068{{title}}\u2069»؟",
    publishBody: "ستراه كل مؤسسة تتابع هذا النطاق (والأزمات تنبّه فرقها).",
    rejectBody: "يختفي من كل المؤسسات، حتى بعد التحديثات القادمة.",
    done: "حُفظ لكل المؤسسات",
    status: { published: "منشور", needs_review: "بحاجة لمراجعة", rejected: "مرفوض", removed: "محذوف" },
  },
  system: {
    title: "النظام والصحة",
    description: "كل الخدمات المعتمَد عليها والطوابير الحية والإصدارات وآخر الإخفاقات.",
    versions: "الإصدارات",
    api: "الواجهة البرمجية",
    node: "Node.js",
    env: "البيئة",
    schema: "مخطط قاعدة البيانات",
    errors: "آخر الإخفاقات",
    errorsHint: "آخر الإخفاقات المحفوظة في الويبهوك والعمليات والإجراءات الخارجية وتشغيلات الأتمتة.",
    noErrors: "لا إخفاقات حديثة.",
  },
};
